<script setup lang="ts">
import { computed, useSlots } from 'vue';

import { AppButton, AppSvgIcon } from '@libs/ui';

interface AlternativeSuggestionHeaderProps {
  infoText: string;
}

const props = defineProps<AlternativeSuggestionHeaderProps>();

interface AlternativeSuggestionHeaderEmits {
  (event: 'confirm'): void;
}

const emit = defineEmits<AlternativeSuggestionHeaderEmits>();

const slots = useSlots();

const hasSecondaryButton = computed(() => !!slots['secondary-button']);
</script>

<template>
  <div
    class="alternative-suggestion-header"
    :class="{ 'has-secondary-button': hasSecondaryButton }"
  >
    <AppSvgIcon name="infoCircle" size="16" />
    <span class="info" :class="{ 'has-secondary-button': hasSecondaryButton }">
      {{ props.infoText }}
      <div
        class="buttons-wrapper"
        :class="{ 'has-secondary-button': hasSecondaryButton }"
      >
        <slot name="secondary-button" />
        <AppButton
          type="primary"
          state="enabled"
          size="small"
          @click="emit('confirm')"
        >
          {{ $t('planner.no_availability.actions.confirm') }}
        </AppButton>
      </div>
    </span>
  </div>
</template>

<style scoped lang="scss">
.alternative-suggestion-header,
.alternative-suggestion-header.has-secondary-button {
  background: var(--color-brand-summer);
  padding: var(--spacing-12);
  display: flex;
  align-items: center;
  gap: var(--spacing-12);

  @include for-less-or-equal-than-599-px {
    align-items: center;
  }

  .info {
    justify-self: start;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-12);
    @include font-label-s-light;
    flex: 1;
  }

  .info.has-secondary-button {
    @include for-less-or-equal-than-599-px {
      flex-direction: column;
    }
  }

  .buttons-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-16);

    * {
      flex-shrink: 1;
      white-space: nowrap;
    }
  }

  .buttons-wrapper.has-secondary-button {
    flex-shrink: 1;

    @include for-less-or-equal-than-599-px {
      width: 100%;
      justify-content: space-between;
    }
  }
}

.alternative-suggestion-header.has-secondary-button {
  @include for-less-or-equal-than-599-px {
    align-items: start;
  }
}
</style>
